import { useState } from "react";
import InfoLayout from "../components/InfoLayout";
import { RelatedFreeTools, toolPageSchemas } from "../components/FreeToolSeo";
import usePageMeta from "../hooks/usePageMeta";
import taxCss from "../styles/tax-calculator.css?raw";
import { cleanRegistration, isValidUkRegistration } from "../utils/registration";

// Three genuinely different pages, not one template with the brand name
// swapped in: different framing, different structure, different UI pattern,
// and a different reg-entry box style per brand, so this doesn't read as
// templated/duplicate content across the 3 domains. ServiceBoss: a
// comparison table + skeuomorphic plate input. ServiceByReg: Q&A + a
// rounded search pill. ServiceLad: a numbered checklist + a plain
// underline field built into the steps themselves. None of the three
// claim a free full check we don't actually offer, all three point to the
// tools that genuinely are free (tax calculator, insurance group checker)
// and the box submits straight to the real reg lookup (checkout, where the
// plan gets chosen), not an invented free-report step.

function useRegBox() {
  const [reg, setReg] = useState("");
  const [error, setError] = useState("");

  const onSubmit = (event) => {
    event.preventDefault();
    const clean = cleanRegistration(reg);
    if (!isValidUkRegistration(clean)) {
      setError("Enter a valid UK registration to continue.");
      return;
    }
    window.location.href = `/checkout?${new URLSearchParams({ vrm: clean })}`;
  };

  return { reg, setReg, error, setError, onSubmit };
}

function PlateCheckBox({ brand }) {
  const { reg, setReg, error, setError, onSubmit } = useRegBox();
  return (
    <div className="tax-form-card" style={{ margin: "22px 0 30px" }}>
      <form onSubmit={onSubmit} noValidate>
        <label htmlFor="fcc-reg">Check a specific vehicle</label>
        <div className="tax-plate-input">
          <span className="tax-plate-band" aria-hidden="true"><img src="/gb-flag.svg" alt="" />UK</span>
          <input
            id="fcc-reg"
            value={reg}
            maxLength="8"
            autoComplete="off"
            placeholder="ENTER REG"
            onChange={(event) => { setReg(cleanRegistration(event.target.value)); setError(""); }}
          />
        </div>
        <button type="submit">See what {brand.name} can find</button>
        <div className="tax-form-error" role="alert">{error}</div>
      </form>
    </div>
  );
}

function PillCheckBox({ brand }) {
  const { reg, setReg, error, setError, onSubmit } = useRegBox();
  return (
    <>
      <form className="pill-check-form" onSubmit={onSubmit} noValidate aria-label={`Check a vehicle with ${brand.name}`}>
        <input
          value={reg}
          maxLength="8"
          autoComplete="off"
          placeholder="Enter a UK reg"
          onChange={(event) => { setReg(cleanRegistration(event.target.value)); setError(""); }}
        />
        <button type="submit">Check</button>
      </form>
      {error && <div className="pill-check-error" role="alert">{error}</div>}
    </>
  );
}

function UnderlineCheckBox({ brand }) {
  const { reg, setReg, error, setError, onSubmit } = useRegBox();
  return (
    <>
      <form className="underline-check-form" onSubmit={onSubmit} noValidate aria-label={`Check a vehicle with ${brand.name}`}>
        <input
          value={reg}
          maxLength="8"
          autoComplete="off"
          placeholder="AB12CDE"
          onChange={(event) => { setReg(cleanRegistration(event.target.value)); setError(""); }}
        />
        <button type="submit">Check →</button>
      </form>
      {error && <div className="underline-check-error" role="alert">{error}</div>}
    </>
  );
}

function ServiceBossVariant({ brand }) {
  return (
    <>
      <h2>Two different records, often confused</h2>
      <p>"Free car check" usually means one of two different things. There's the free, official record DVLA and DVSA publish for any UK reg. And there's manufacturer service history, the dealer-network record of what work was actually carried out, which is a separate thing entirely and not part of the free public record.</p>

      <div className="compare-wrap">
        <table className="compare-table">
          <thead><tr><th>Record</th><th>Shows</th><th>Free?</th></tr></thead>
          <tbody>
            <tr><td>DVLA vehicle record</td><td>Tax status, MOT status, basic vehicle facts</td><td>Yes, always</td></tr>
            <tr><td>DVSA MOT history</td><td>Past test results, advisories, mileage at each test</td><td>Yes, always</td></tr>
            <tr><td>Manufacturer service history</td><td>Dealer-recorded servicing: dates, mileage, garage, work carried out</td><td>Not part of the free public record</td></tr>
          </tbody>
        </table>
      </div>

      <h2>Why the manufacturer record matters more for older claims</h2>
      <p>A seller claiming "full main dealer history" is making a specific, checkable claim, and the manufacturer record is the fastest way to confirm it, since it comes straight from the dealer network rather than the seller's word. That's the gap {brand.name} was built to close: pulling that record together by registration, alongside the mileage and garage detail that a free check alone won't show.</p>

      <PlateCheckBox brand={brand} />

      <div className="plain-note">Coverage depends on the vehicle. Enter a reg above to see what's actually on record for a specific car rather than going by the general picture here.</div>
    </>
  );
}

function ServiceByRegVariant({ brand }) {
  return (
    <>
      <h2>The questions people actually ask</h2>
      <div className="guide-faq">
        <h3>Is there a free way to check a car before buying?</h3>
        <p>Yes, for the basics. DVLA's record (tax and MOT status, basic vehicle facts) and DVSA's MOT history (past tests, advisories, recorded mileage) are both free and official, no account or payment needed.</p>

        <h3>Does a free check cover manufacturer service history?</h3>
        <p>No. Service history, dates, mileage, garage and work carried out as recorded by the manufacturer's own dealer network, sits behind a paid report, same as it does everywhere else. It's the main thing {brand.name}'s report actually pulls together, with a finance, write-off and stolen check included alongside it.</p>

        <h3>Does a free check cover finance, write-off or stolen status?</h3>
        <p>No. Those come from insurer and finance-house data, not the public record, so a free basic check won't show them either, that's a separate gap on top of the service-history one above.</p>

        <h3>What can {brand.name} check for free right now?</h3>
        <p>The <a href="/car-tax-calculator/">tax calculator</a> and the <a href="/insurance-groups">insurance group checker</a> are both genuinely free, no payment, no account. Service history plus the finance, write-off and stolen check is the paid report, since it draws on dealer, insurer and finance-house data the free public record doesn't include.</p>

        <h3>How do I find out what's available for a specific car?</h3>
        <p>Enter the registration below and look, rather than go by a general answer. Coverage varies by vehicle.</p>
      </div>

      <PillCheckBox brand={brand} />
    </>
  );
}

function ServiceLadVariant({ brand }) {
  return (
    <>
      <h2>A free check is a snapshot, not a subscription</h2>
      <p>Whatever you check for free today is only accurate for that moment. A car can look clean on a free check and have something change the next week, a new keeper, a finance agreement, a fresh V5C. That's the distinction {brand.name} is actually built around: a one-off check versus watching a registration over time.</p>

      <ol className="check-steps">
        <li><strong>Check DVLA's free record</strong><p>Tax status, MOT status and basic vehicle facts, official and free by registration.</p></li>
        <li><strong>Check DVSA's free MOT history</strong><p>Past test results, advisories and mileage at each test, also free and official.</p></li>
        <li><strong>Run {brand.name}'s free tools for more</strong><p>The <a href="/car-tax-calculator/">tax calculator</a> and <a href="/insurance-groups">insurance group checker</a> add to the free picture at no cost.</p></li>
        <li>
          <strong>Check a specific reg now</strong>
          <p>Enter it below rather than going by a general answer, coverage varies car to car.</p>
          <UnderlineCheckBox brand={brand} />
        </li>
      </ol>

      <div className="callout callout-tip">
        <span className="callout-icon" aria-hidden="true">💡</span>
        <div>
          <strong>Bought the car already?</strong>
          <p><a href="/v5c-alerts">V5C alerts</a> watch the registration afterward, catching a change a one-off check by definition can't.</p>
        </div>
      </div>
    </>
  );
}

const brandCopy = {
  serviceboss: {
    title: "Free car check: the free record vs manufacturer service history",
    intro: "Two different things get called a \"free car check.\" Here's the difference, and where each one actually comes from.",
    metaTitle: "Free Car Check UK: What's Free vs Paid | ServiceBoss",
    metaDescription: "Free car check UK explained. What DVLA and DVSA show for free, what manufacturer service history costs, and how to check a reg with ServiceBoss.",
    Variant: ServiceBossVariant,
  },
  servicebyreg: {
    title: "Free car check: your questions answered",
    intro: "What's actually free, what isn't, and where to look for each, answered directly.",
    metaTitle: "Free Car Check UK: Questions Answered | ServiceByReg",
    metaDescription: "Free car check UK questions answered. What's free, what needs a paid risk check, and how to check a registration with ServiceByReg.",
    Variant: ServiceByRegVariant,
  },
  servicelad: {
    title: "Free car check: a step-by-step starting point",
    intro: "A free check tells you what's true right now. Here's how to run one properly, and when to keep watching afterward.",
    metaTitle: "Free Car Check UK: Step by Step | ServiceLad",
    metaDescription: "Free car check UK, step by step. What to check for free, and why ongoing monitoring catches what a one-off check can't. From ServiceLad.",
    Variant: ServiceLadVariant,
  },
};

export default function FreeCarCheckPage({ brand }) {
  const copy = brandCopy[brand.key] || brandCopy.serviceboss;
  const Variant = copy.Variant;
  const canonical = `https://${brand.domain}/free-car-check`;
  usePageMeta({
    title: copy.metaTitle,
    description: copy.metaDescription,
    canonical,
  });

  const schema = {
    "@context": "https://schema.org",
    "@graph": toolPageSchemas({
      brand,
      canonical,
      name: `Free Car Check UK | ${brand.name}`,
      description: copy.metaDescription,
      alternateName: [
        "Free car check",
        "Free UK car check",
        "Free vehicle check",
        "Free car check by registration",
        "What does a free car check show",
      ],
      pathLabel: "Free car check",
    }),
  };

  return (
    <InfoLayout brand={brand} eyebrow="Free car check" title={copy.title} intro={copy.intro}>
      <style>{taxCss}</style>
      <script type="application/ld+json">{JSON.stringify(schema)}</script>
      <section className="content">
        <div className="wrap prose">
          <Variant brand={brand} />
          <RelatedFreeTools current="freeCheck" />
        </div>
      </section>
    </InfoLayout>
  );
}
